import { Meeting } from '../../queries/meetings';

export interface Readiness {
  label: string;
  ready: boolean;
}

/** A meeting is "Ready" once odds, form data, and track conditions have all landed. */
export function getMeetingReadiness(meeting: Meeting): Readiness {
  const dc = meeting.data_completeness;
  if (!dc?.has_odds || !dc?.has_form_data || !dc?.has_track_conditions) {
    return { label: 'Missing Data', ready: false };
  }
  return { label: 'Ready', ready: true };
}

export type MeetingFilter = 'all' | 'ready' | 'missing';

export function filterMeetings(meetings: Meeting[], filter: MeetingFilter): Meeting[] {
  if (filter === 'all') return meetings;
  return meetings.filter((m) => getMeetingReadiness(m).ready === (filter === 'ready'));
}

export function getConditionStyle(condition: string | null): string {
  const c = condition?.toLowerCase() ?? '';
  if (c.startsWith('good') || c === 'firm') {
    return 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/20 dark:text-emerald-400 dark:border-emerald-900';
  }
  if (c.startsWith('soft')) {
    return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/20 dark:text-blue-400 dark:border-blue-900';
  }
  if (c.startsWith('heavy')) {
    return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-950/20 dark:text-red-400 dark:border-red-900';
  }
  if (c === 'synthetic') {
    return 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/20 dark:text-purple-400 dark:border-purple-900';
  }
  return 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-gray-800/40 dark:text-gray-400 dark:border-gray-700';
}

export type WeatherIconKind = 'rain' | 'cloud' | 'sun';

/** Icon choice as data, not JSX — the component maps this to a lucide icon. */
export function getWeatherIconKind(weather: string | null): WeatherIconKind {
  const w = weather?.toLowerCase() ?? '';
  if (w.includes('rain') || w.includes('shower')) return 'rain';
  if (w.includes('fine') || w.includes('sun') || w.includes('clear')) return 'sun';
  return 'cloud';
}

export function formatConditionLabel(tc: Meeting['track_condition_details']): string | null {
  if (!tc?.track_condition) return null;
  return [tc.track_condition, tc.track_condition_number].filter(Boolean).join(' ');
}

export interface DriftBadge {
  label: string;
  className: string;
  /** The track is wetter than predicted — the tips were generated on the wrong going. */
  alert: boolean;
}

/**
 * The going against the prediction the models and the site were given. The
 * API only sends condition_drift when the two disagree and both are rated
 * (changeset 0034), so any drift here is worth showing; `alert` marks the one
 * direction that invalidates a tip — the track gone the wrong way.
 */
export function getConditionDrift(meeting: Meeting): DriftBadge | null {
  const d = meeting.condition_drift;
  if (!d) return null;
  const predicted = [d.predicted_track_condition, d.predicted_track_condition_number].filter(Boolean).join(' ');
  if (d.is_deteriorating) {
    return {
      label: `Worse than predicted ${predicted}`,
      className: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900',
      alert: true,
    };
  }
  if (d.grade_change < 0) {
    return {
      label: `Better than predicted ${predicted}`,
      className: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/20 dark:text-emerald-400 dark:border-emerald-900',
      alert: false,
    };
  }
  return {
    label: `Predicted ${predicted}`,
    className: 'bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-800/40 dark:text-gray-400 dark:border-gray-700',
    alert: false,
  };
}

export interface StatusBadge {
  label: string;
  className: string;
}

export function getStatusBadge(status: string | null): StatusBadge {
  if (status === 'scheduled') {
    return { label: 'Scheduled', className: 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-400' };
  }
  if (status === 'in_progress') {
    return { label: 'In Progress', className: 'bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-400' };
  }
  if (status === 'completed') {
    return { label: 'Completed', className: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400' };
  }
  return { label: 'Unknown', className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-400' };
}

// ponytail: date-fns isn't a dependency of admin-react (unlike the old Next.js admin), so
// date arithmetic is done with plain local Date components instead of pulling it in.
export function formatDateLocal(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function todayDateString(): string {
  return formatDateLocal(new Date());
}

/** Shifts a yyyy-MM-dd string by N calendar days, in local time (matches native `<input type="date">`). */
export function shiftDate(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  date.setDate(date.getDate() + days);
  return formatDateLocal(date);
}

// punters.com.au names each race download yyyyMMdd-track-name-rNN.csv; the
// race number comes from the trailing rNN so a whole meeting can be dropped
// into the odds uploader at once.
export function raceNumberFromFilename(name: string): number | null {
  const match = name.match(/-r(\d{1,2})\.csv$/i);
  return match ? parseInt(match[1], 10) : null;
}
